import React, { useState, useEffect, useCallback } from "react";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import api from "../services/api";
import {
  MoneyRecive,
  ShoppingCart,
  Clock,
  Warning2,
  Refresh2,
  TrendUp,
  TickCircle,
  Calendar,
} from "iconsax-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [chartPeriod, setChartPeriod] = useState("30"); // "7" | "30" | "90"
  const [chartData, setChartData] = useState(null);

  // Period filter state
  const todayStr = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Makassar" });
  const [period, setPeriod] = useState("today"); // preset key
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);
  const [showCustom, setShowCustom] = useState(false);

  const periodPresets = [
    { key: "today", label: "Hari Ini" },
    { key: "yesterday", label: "Kemarin" },
    { key: "week", label: "7 Hari" },
    { key: "month", label: "Bulan Ini" },
    { key: "lastmonth", label: "Bulan Lalu" },
    { key: "custom", label: "Custom" },
  ];

  const applyPreset = (key) => {
    const d = new Date();
    const fmt = (dt) => dt.toLocaleDateString("sv-SE", { timeZone: "Asia/Makassar" });
    let from, to;
    if (key === "today") {
      from = to = fmt(d);
    } else if (key === "yesterday") {
      const y = new Date(d);
      y.setDate(d.getDate() - 1);
      from = to = fmt(y);
    } else if (key === "week") {
      const w = new Date(d);
      w.setDate(d.getDate() - 6);
      from = fmt(w);
      to = fmt(d);
    } else if (key === "month") {
      from = fmt(new Date(d.getFullYear(), d.getMonth(), 1));
      to = fmt(d);
    } else if (key === "lastmonth") {
      const lm = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const lme = new Date(d.getFullYear(), d.getMonth(), 0);
      from = fmt(lm);
      to = fmt(lme);
    } else {
      return; // custom handled separately
    }
    setPeriod(key);
    setDateFrom(from);
    setDateTo(to);
    setShowCustom(false);
  };

  const applyCustom = () => {
    setDateFrom(customFrom);
    setDateTo(customTo);
    setPeriod("custom");
    setShowCustom(false);
  };

  const periodLabel = () => {
    if (period === "custom") {
      if (dateFrom === dateTo) return dateFrom;
      return `${dateFrom} – ${dateTo}`;
    }
    return periodPresets.find((p) => p.key === period)?.label || "";
  };

  const loadDashboard = useCallback(
    async (silent = false, from = dateFrom, to = dateTo) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const res = await api.get(
          `/reports/dashboard?date_from=${from}&date_to=${to}`,
        );
        setData(res.data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Dashboard error:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [dateFrom, dateTo],
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Re-slice chart data when period or data changes
  useEffect(() => {
    if (!data?.monthlySales) return;
    // When using a custom period, show all data returned. For "today" preset use the 7/30/90 toggle.
    const sliced =
      period === "today"
        ? data.monthlySales.slice(-parseInt(chartPeriod))
        : data.monthlySales;
    const pts = sliced.length;
    setChartData({
      labels: sliced.map((d) => d.date),
      datasets: [
        {
          label: "Penjualan",
          data: sliced.map((d) => d.sales),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.08)",
          fill: true,
          tension: 0.4,
          pointRadius: pts <= 7 ? 4 : 2,
          pointHoverRadius: 6,
        },
      ],
    });
  }, [data, chartPeriod, period]);

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 320 }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-2"></div>
          <div className="text-muted small">Memuat dashboard...</div>
        </div>
      </div>
    );
  if (!data)
    return <div className="alert alert-danger">Gagal memuat dashboard</div>;

  const today = data.today || {};
  const yesterday = data.yesterday || {};
  const thisMonth = data.thisMonth || {};
  const lastMonth = data.lastMonth || {};
  const periodSales = data.periodSales || {};
  const unpaid = data.unpaid || {};

  const pctChange = (current, prev) => {
    const c = Number(current || 0);
    const p = Number(prev || 0);
    if (p === 0) return c > 0 ? 100 : null;
    return Math.round(((c - p) / p) * 100);
  };
  const lowStock = data.lowStock || [];
  const topServices = data.topServices || [];
  const topProducts = data.topProducts || [];
  const byServiceCategory = data.byServiceCategory || [];
  const byInventoryCategory = data.byInventoryCategory || [];
  const byPaymentMethod = data.byPaymentMethod || [];
  const byStatusActive = data.byStatusActive || [];

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, font: { size: 11 } },
      },
    },
    cutout: "62%",
  };

  const isToday = period === "today";
  const pLabel = periodLabel();

  const statCards = [
    {
      label: isToday ? "Penjualan Hari Ini" : `Penjualan (${pLabel})`,
      value: fmt(isToday ? today.total_sales : periodSales.total_sales),
      change: isToday
        ? pctChange(today.total_sales, yesterday.total_sales)
        : null,
      compareLabel: "vs kemarin",
      icon: <MoneyRecive size={22} color="#fff" variant="Bold" />,
      bg: "#3b82f6",
    },
    {
      label: isToday ? "Transaksi Hari Ini" : `Transaksi (${pLabel})`,
      value: isToday
        ? (today.total_transactions ?? 0)
        : (periodSales.total_transactions ?? 0),
      change: isToday
        ? pctChange(today.total_transactions, yesterday.total_transactions)
        : null,
      compareLabel: "vs kemarin",
      icon: <ShoppingCart size={22} color="#fff" variant="Bold" />,
      bg: "#10b981",
    },
    ...(isToday
      ? [
          {
            label: "Penjualan Bulan Ini",
            value: fmt(thisMonth.total_sales),
            change: pctChange(thisMonth.total_sales, lastMonth.total_sales),
            compareLabel: "vs bulan lalu",
            icon: <MoneyRecive size={22} color="#fff" variant="Bold" />,
            bg: "#8b5cf6",
          },
          {
            label: "Transaksi Bulan Ini",
            value: thisMonth.total_transactions ?? 0,
            change: pctChange(
              thisMonth.total_transactions,
              lastMonth.total_transactions,
            ),
            compareLabel: "vs bulan lalu",
            icon: <Calendar size={22} color="#fff" variant="Bold" />,
            bg: "#f59e0b",
          },
        ]
      : []),
    {
      label: "Tagihan Belum Dibayar",
      value: fmt(unpaid.total_sales),
      change: null,
      icon: <Warning2 size={22} color="#fff" variant="Bold" />,
      bg: "#ef4444",
    },
    {
      label: "Pesanan Belum Dibayar",
      value: unpaid.total_transactions ?? 0,
      change: null,
      icon: <Clock size={22} color="#fff" variant="Bold" />,
      bg: "#f97316",
    },
  ];

  const fmtTime = (d) =>
    d
      ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
      : "";

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="fw-bold mb-0">Dashboard</h5>
          {lastUpdated && (
            <small className="text-muted">
              Diperbarui {fmtTime(lastUpdated)}
            </small>
          )}
        </div>
        <button
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
          onClick={() => loadDashboard(true)}
          disabled={refreshing}
          title="Refresh data"
        >
          <Refresh2 size={16} className={refreshing ? "spin-icon" : ""} />
          {refreshing ? "Memuat..." : "Refresh"}
        </button>
      </div>

      {/* Period Selector */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="text-muted small fw-semibold me-1">Periode:</span>
            {periodPresets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`btn btn-sm ${period === p.key ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => {
                  if (p.key === "custom") {
                    setShowCustom((v) => !v);
                  } else {
                    applyPreset(p.key);
                  }
                }}
              >
                {p.label}
              </button>
            ))}
            {period !== "today" && (
              <span
                className="badge bg-light text-dark border ms-1"
                style={{ fontSize: "0.78rem" }}
              >
                {periodLabel()}
              </span>
            )}
          </div>
          {showCustom && (
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={customFrom}
                max={customTo}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span className="text-muted small">s/d</span>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
              />
              <button className="btn btn-sm btn-primary" onClick={applyCustom}>
                Terapkan
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="row g-3 mb-3">
        {statCards.map((c, i) => (
          <div key={i} className={`col-6 ${isToday ? "col-md-2" : "col-md-3"}`}>
            <div
              className="card border-0 h-100"
              style={{
                background: `linear-gradient(135deg, ${c.bg}ee, ${c.bg}cc)`,
              }}
            >
              <div className="card-body text-white p-3">
                <div className="mb-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 38,
                      height: 38,
                      background: "rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  >
                    {c.icon}
                  </div>
                </div>
                <div
                  className="fw-bold mb-1"
                  style={{ fontSize: "1.15rem", lineHeight: 1.1 }}
                >
                  {c.value}
                </div>
                <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                  {c.label}
                </div>
                {c.change !== null && c.change !== undefined && (
                  <div
                    style={{ fontSize: "0.68rem", opacity: 0.7, marginTop: 2 }}
                  >
                    {c.change >= 0 ? "+" : ""}
                    {c.change}% {c.compareLabel}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Status Order Aktif */}
      <div className="row g-2 mb-4">
        {[
          { status: "diterima", label: "Diterima", color: "#3b82f6" },
          { status: "dicuci", label: "Dicuci", color: "#06b6d4" },
          { status: "disetrika", label: "Disetrika", color: "#f97316" },
          { status: "siap_ambil", label: "Siap Ambil", color: "#10b981" },
        ].map(({ status, label, color }) => {
          const found = byStatusActive.find((s) => s.status === status);
          const count = found ? found.count : 0;
          return (
            <div key={status} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body py-2 px-3 d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                    style={{
                      width: 40,
                      height: 40,
                      background: color + "20",
                      color,
                      fontSize: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    {count}
                  </div>
                  <div>
                    <div
                      className="fw-semibold"
                      style={{ fontSize: "0.82rem" }}
                    >
                      {label}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      order aktif
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart + Low Stock */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  Tren Penjualan{" "}
                  {period === "today" ? "" : `(${periodLabel()})`}
                </h6>
                {period === "today" && (
                  <div className="btn-group btn-group-sm" role="group">
                    {[
                      ["7", "7H"],
                      ["30", "30H"],
                      ["90", "90H"],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        className={`btn ${
                          chartPeriod === v
                            ? "btn-primary"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => setChartPeriod(v)}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {chartData && (
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (v) =>
                            "Rp " + Number(v).toLocaleString("id-ID"),
                          font: { size: 10 },
                        },
                        grid: { color: "rgba(0,0,0,0.05)" },
                      },
                      x: { grid: { display: false } },
                    },
                  }}
                  style={{ minHeight: 200, maxHeight: 240 }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Warning2 size={18} color="#f59e0b" variant="Bold" />
                <h6 className="fw-bold mb-0">Stok Menipis</h6>
                {lowStock.length > 0 && (
                  <span className="badge bg-danger rounded-pill ms-auto">
                    {lowStock.length}
                  </span>
                )}
              </div>
              {lowStock.length === 0 ? (
                <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center gap-2">
                  <TickCircle
                    size="20%"
                    color="#10b981"
                    variant="Bold"
                    style={{ minWidth: 64, maxWidth: 96 }}
                  />
                  <div className="text-muted small">Semua stok aman</div>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {lowStock.map((p) => (
                    <div
                      key={p.id}
                      className="list-group-item d-flex justify-content-between align-items-center px-0 py-2"
                    >
                      <div>
                        <div
                          className="small fw-semibold text-truncate"
                          style={{ maxWidth: 150 }}
                        >
                          {p.name}
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "0.7rem" }}
                        >
                          SKU: {p.sku || "-"}
                        </div>
                      </div>
                      <span className="badge bg-danger rounded-pill">
                        {p.stock} {p.unit}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top Services + Products */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                Layanan Terlaris{" "}
                {period === "today" ? "Hari Ini" : `(${periodLabel()})`}
              </h6>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr className="table-light">
                      <th style={{ width: 24 }}>#</th>
                      <th>Layanan</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topServices.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          Belum ada data pada periode ini
                        </td>
                      </tr>
                    ) : (
                      topServices.map((s, i) => (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{s.item_name}</td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {s.total_qty}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">
                            {fmt(s.total_sales)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                Produk Terlaris{" "}
                {period === "today" ? "Hari Ini" : `(${periodLabel()})`}
              </h6>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr className="table-light">
                      <th style={{ width: 24 }}>#</th>
                      <th>Produk</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          Belum ada data pada periode ini
                        </td>
                      </tr>
                    ) : (
                      topProducts.map((p, i) => (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>
                          <td>{p.item_name}</td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">
                              {p.total_qty}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">
                            {fmt(p.total_sales)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Charts */}
      <div className="row g-3">
        {/* Per Kategori Layanan */}
        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Kategori Layanan</h6>
              {byServiceCategory.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada data pada periode ini
                </div>
              ) : (
                <>
                  <div style={{ maxWidth: 200, margin: "0 auto" }}>
                    <Doughnut
                      data={{
                        labels: byServiceCategory.map((v) => v.category_name),
                        datasets: [
                          {
                            data: byServiceCategory.map((v) =>
                              Number(v.total_sales),
                            ),
                            backgroundColor: COLORS,
                          },
                        ],
                      }}
                      options={doughnutOpts}
                    />
                  </div>
                  <div className="mt-3">
                    {byServiceCategory.map((v, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background: COLORS[i % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">{v.category_name}</span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(v.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {v.total_transactions} trx
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Per Kategori Inventaris */}
        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Kategori Produk</h6>
              {byInventoryCategory.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada penjualan produk pada periode ini
                </div>
              ) : (
                <>
                  <div style={{ maxWidth: 200, margin: "0 auto" }}>
                    <Doughnut
                      data={{
                        labels: byInventoryCategory.map((c) => c.category_name),
                        datasets: [
                          {
                            data: byInventoryCategory.map((c) =>
                              Number(c.total_sales),
                            ),
                            backgroundColor: COLORS.slice().reverse(),
                          },
                        ],
                      }}
                      options={doughnutOpts}
                    />
                  </div>
                  <div className="mt-3">
                    {byInventoryCategory.map((c, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background:
                                COLORS[(COLORS.length - 1 - i) % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">{c.category_name}</span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(c.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {c.total_qty} unit
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Per Metode Pembayaran */}
        <div className="col-12 col-md-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-bold mb-3">Per Metode Pembayaran</h6>
              {byPaymentMethod.length === 0 ? (
                <div className="text-center text-muted py-4 small">
                  Belum ada data pada periode ini
                </div>
              ) : (
                <>
                  <Bar
                    data={{
                      labels: byPaymentMethod.map((pm) =>
                        pm.payment_method === "unpaid"
                          ? "Belum Bayar"
                          : (pm.payment_method || "").toUpperCase(),
                      ),
                      datasets: [
                        {
                          label: "Total",
                          data: byPaymentMethod.map((pm) =>
                            Number(pm.total_sales),
                          ),
                          backgroundColor: COLORS,
                          borderRadius: 6,
                        },
                      ],
                    }}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: {
                            callback: (v) =>
                              "Rp " + Number(v).toLocaleString("id-ID"),
                            font: { size: 10 },
                          },
                          grid: { color: "rgba(0,0,0,0.05)" },
                        },
                        y: { grid: { display: false } },
                      },
                    }}
                  />
                  <div className="mt-2">
                    {byPaymentMethod.map((pm, i) => (
                      <div
                        key={i}
                        className="d-flex justify-content-between align-items-center py-1 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle"
                            style={{
                              width: 8,
                              height: 8,
                              background: COLORS[i % COLORS.length],
                              display: "inline-block",
                              flexShrink: 0,
                            }}
                          />
                          <span className="small">
                            {pm.payment_method === "unpaid"
                              ? "Belum Bayar"
                              : (pm.payment_method || "").toUpperCase()}
                          </span>
                        </div>
                        <div className="text-end">
                          <div className="small fw-semibold">
                            {fmt(pm.total_sales)}
                          </div>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {pm.total_transactions} trx
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
